/**
 * Hook para execução do chat público do fluxo
 * Gerencia sessão do visitante, execução de nós e persistência
 * Suporta todos os tipos de nós do FlowBuilder
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { getUTMParams } from '@/lib/utmCapture';
import { normalizeEmbedUrl } from '@/lib/embed-utils';
import { applyVariableFormat } from '@/lib/variableFormatter';

// Interfaces
interface FlowNode {
  id: string;
  type: string;
  data: Record<string, unknown>;
  position: { x: number; y: number };
}

interface FlowEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string;
}

interface FlowData {
  id: string;
  name: string;
  nodes: FlowNode[];
  edges: FlowEdge[];
  variables: Array<{ name: string; type: string; defaultValue?: string }>;
}

/**
 * Interface de configuração visual do chat
 * Inclui todos os campos de template para renderização premium
 */
interface ChatConfig {
  // Campos básicos
  primary_color: string;
  background_color: string;
  bot_avatar_enabled: boolean; // Controla exibição do avatar (se true sem URL, mostra ícone padrão)
  bot_avatar_url: string | null;
  user_avatar_enabled: boolean;
  show_branding: boolean;
  company_name: string;
  company_logo_url: string | null;
  max_width: string;
  custom_css: string | null;
  // Campos de template para visual premium
  template_id: string | null;
  header_bg_color: string;
  header_text_color: string;
  user_bubble_color: string;
  bot_bubble_color: string;
  user_text_color: string;
  bot_text_color: string;
  border_radius: string;
  background_image: string | null;
}

export interface ChatMessage {
  id: string;
  type: 'bot' | 'user' | 'system';
  content: string;
  timestamp: Date;
  buttons?: Array<{ id: string; text: string }>;
  nodeId?: string;
  // Campos para mídia e lista
  mediaType?: 'image' | 'video' | 'audio' | 'document' | 'buttons' | 'list' | 'embed';
  mediaUrl?: string;
  caption?: string;
  duration?: number;
  filename?: string;
  mimeType?: string;
  listOptions?: Array<{ id: string; label: string; description?: string }>;
  listTitle?: string;
  /** Altura do iframe para embed (em px) */
  embedHeight?: number;
}

/**
 * Interface de dados de rastreamento do fluxo
 * Retornado pela RPC get_public_flow_by_slug
 */
interface TrackingConfig {
  gtm_id: string | null;
  meta_pixel_id: string | null;
  ga4_measurement_id: string | null;
  custom_head_code: string | null;
}

interface PublicFlowData {
  flow: FlowData;
  config: ChatConfig;
  tenant_name: string;
  tracking?: TrackingConfig;
}

// Gera ID único para visitante
const generateVisitorId = (): string => {
  const stored = localStorage.getItem('flow_chat_visitor_id');
  if (stored) return stored;
  
  const newId = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  localStorage.setItem('flow_chat_visitor_id', newId);
  return newId;
};

/**
 * Hook principal para chat público do fluxo
 * @param tenantSlug - Slug do tenant (identificador único)
 * @param flowSlug - Slug do fluxo público
 */
export function usePublicFlowChat(tenantSlug: string | undefined, flowSlug: string | undefined) {
  const [flowData, setFlowData] = useState<PublicFlowData | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [currentNodeId, setCurrentNodeId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [waitingForInput, setWaitingForInput] = useState(false);
  
  const visitorId = useRef(generateVisitorId());
  const executionQueue = useRef<string[]>([]);
  const isProcessing = useRef(false);

  // Ref espelhando waitingForInput para evitar stale state em callbacks (setState é assíncrono)
  const waitingForInputRef = useRef(false);
  
  // Ref para evitar carregamento duplicado (corrige loop infinito)
  const isLoadedRef = useRef(false);
  
  // Ref para processNode (evita dependência circular no loadFlow)
  const processNodeRef = useRef<((node: FlowNode, flowDataRef: PublicFlowData, currentVariables: Record<string, string>) => Promise<void>) | null>(null);

  // Substitui variáveis no texto
  const substituteVariables = useCallback((text: string): string => {
    if (!text) return '';
    
    // Suporta sintaxe {{variavel||formato}} para formatação inline
    return text.replace(/\{\{(\w+(?:\.\w+)*)(?:\|\|([^}]+))?\}\}/g, (match, varName, fmt) => {
      const rawValue = variables[varName];
      if (rawValue === undefined) return match;
      if (fmt) {
        return applyVariableFormat(rawValue, fmt);
      }
      return String(rawValue);
    });
  }, [variables]);

  // Adiciona mensagem ao chat
  const addMessage = useCallback((message: ChatMessage) => {
    setMessages((prev) => [...prev, message]);
  }, []);

  // Mantém o ref sincronizado com o estado para evitar "stale closure" em callbacks
  useEffect(() => {
    waitingForInputRef.current = waitingForInput;
  }, [waitingForInput]);

  // Move para próximo nó
  const moveToNextNode = useCallback((currentId: string, flowDataRef: PublicFlowData | null) => {
    if (!flowDataRef) return;

    const edge = flowDataRef.flow.edges.find((e) => e.source === currentId);
    if (edge) {
      executionQueue.current.push(edge.target);
    } else {
      // Sem próximo nó - fluxo terminou
      setIsCompleted(true);
    }
  }, []);

  // Processa nó de mensagem (message, message_text, message_media)
  const processMessageNode = useCallback(async (
    nodeId: string, 
    data: Record<string, unknown>,
    flowDataRef: PublicFlowData
  ) => {
    setIsTyping(true);
    
    // Simula tempo de digitação
    await new Promise((resolve) => setTimeout(resolve, 800 + Math.random() * 500));
    
    // Suporta tanto 'message' quanto 'text' e estrutura config.text
    const config = (data.config as Record<string, unknown>) || {};
    const content = substituteVariables(
      (config.text as string) || 
      (data.text as string) || 
      (data.message as string) || 
      ''
    );
    
    // Detecta mídia se existir
    const mediaType = (config.media_type as string) || (data.media_type as string);
    const mediaUrl = (config.media_url as string) || (data.media_url as string);
    const filename = (config.filename as string) || (data.filename as string);
    const mimeType = (config.mime_type as string) || (data.mime_type as string);
    const duration = (config.duration as number) || (data.duration as number);
    
    if (content || mediaUrl) {
      addMessage({
        id: `msg_${Date.now()}`,
        type: 'bot',
        content,
        timestamp: new Date(),
        nodeId,
        // Campos de mídia
        mediaType: mediaType as ChatMessage['mediaType'],
        mediaUrl: mediaUrl || undefined,
        caption: content,
        filename: filename || undefined,
        mimeType: mimeType || undefined,
        duration: duration || undefined,
      });
    }

    setIsTyping(false);
    moveToNextNode(nodeId, flowDataRef);
  }, [substituteVariables, addMessage, moveToNextNode]);

  /**
   * Extrai valor de um objeto usando path (suporta dot notation)
   * Ex: "data.user.name", "items.0.title", "veiculos"
   * 
   * RELEASE: 2.2.0 - 2024-12-28 - Função adicionada para suportar options_array_path
   */
  const getNestedValue = useCallback((obj: any, path: string): any => {
    if (!obj || !path) return undefined;
    
    // Normaliza path: "[0].campo" -> "0.campo", "campo[0]" -> "campo.0"
    const normalizedPath = path
      .replace(/\[(\d+)\]/g, '.$1')
      .replace(/^\./, '');
    
    const parts = normalizedPath.split('.');
    let value = obj;
    
    for (const part of parts) {
      if (value === null || value === undefined) return undefined;
      
      const index = parseInt(part, 10);
      if (!isNaN(index) && Array.isArray(value)) {
        value = value[index];
      } else {
        value = value[part];
      }
    }
    
    return value;
  }, []);

  /**
   * Processa nó de pergunta (question, question_buttons, question_list)
   * 
   * RELEASE: 2.2.0 - 2024-12-28 - Adicionado suporte a options_array_path para listas dinâmicas
   */
  const processQuestionNode = useCallback(async (
    nodeId: string, 
    data: Record<string, unknown>,
    flowDataRef: PublicFlowData,
    nodeType: string
  ) => {
    setIsTyping(true);
    await new Promise((resolve) => setTimeout(resolve, 600));

    // Suporta diferentes estruturas de configuração do FlowBuilder
    const config = (data.config as Record<string, unknown>) || {};
    const content = substituteVariables(
      (config.text as string) || 
      (config.question as string) || 
      (data.text as string) ||
      (data.question as string) || 
      (data.message as string) || 
      ''
    );
    
    // Diferencia lista de botões
    if (nodeType === 'question_list') {
      const listTitle = (config.list_title as string) || (data.list_title as string) || 'Selecione uma opção';
      let listOptions: Array<{ id: string; label: string; description?: string }> = [];
      
      // Verifica se as opções vêm de uma variável (lista dinâmica)
      const optionsSource = config.options_source as string;
      const optionsVariable = config.options_variable as string;
      
      if (optionsSource === 'variable' && optionsVariable) {
        // Busca o valor da variável
        let sourceData: any = variables[optionsVariable];
        
        // NOVO: Se tem array_path, navega até o array dentro do objeto
        const arrayPath = config.options_array_path as string;
        if (arrayPath && sourceData && typeof sourceData === 'object') {
          sourceData = getNestedValue(sourceData, arrayPath);
          logger.debug('usePublicFlowChat', `Navegou para array em path: ${arrayPath}`);
        }
        
        if (Array.isArray(sourceData) && sourceData.length > 0) {
          const labelPath = (config.options_label_path as string) || 'label';
          const valuePath = (config.options_value_path as string) || labelPath;
          const descriptionPath = config.options_description_path as string;
          
          listOptions = sourceData.map((item: any, index: number) => {
            const label = getNestedValue(item, labelPath) || `Item ${index + 1}`;
            const value = getNestedValue(item, valuePath) || label;
            const description = descriptionPath ? getNestedValue(item, descriptionPath) : undefined;
            
            return {
              id: String(value),
              label: String(label),
              description: description ? String(description) : undefined
            };
          });
          
          // Aplica distinct se configurado (padrão: true)
          const optionsDistinct = config.options_distinct !== false;
          if (optionsDistinct) {
            const uniqueMap = new Map<string, typeof listOptions[0]>();
            listOptions.forEach(opt => {
              if (!uniqueMap.has(opt.id)) {
                uniqueMap.set(opt.id, opt);
              }
            });
            listOptions = Array.from(uniqueMap.values());
          }
          
          logger.debug('usePublicFlowChat', `Opções dinâmicas carregadas: ${listOptions.length} itens`);
        } else {
          logger.warn('usePublicFlowChat', `Variável de opções vazia ou não é array: ${optionsVariable}`);
        }
      } else {
        // Opções estáticas (comportamento original)
        const rawOptions = (config.options as any[]) || (data.options as any[]) || [];
        listOptions = rawOptions.map((opt: any, index: number) => ({
          id: `option_${index}`,
          label: opt.label || opt.title || opt.text || `Opção ${index + 1}`,
          description: opt.description || '',
        }));
      }
      
      addMessage({
        id: `msg_${Date.now()}`,
        type: 'bot',
        content,
        timestamp: new Date(),
        nodeId,
        mediaType: 'list',
        listTitle,
        listOptions,
      });
    } else {
      // Botões normais
      const rawOptions = (config.options as any[]) || (data.options as any[]) || [];
      const buttons = rawOptions.map((opt: any, index: number) => ({
        id: `option_${index}`,
        text: opt.label || opt.title || opt.text || `Opção ${index + 1}`,
      }));

      addMessage({
        id: `msg_${Date.now()}`,
        type: 'bot',
        content,
        timestamp: new Date(),
        buttons: buttons.length > 0 ? buttons : undefined,
        nodeId,
        mediaType: 'buttons',
      });
    }

    setIsTyping(false);
    setWaitingForInput(true);
  }, [substituteVariables, addMessage, variables, getNestedValue]);

  // Processa nó de input (input, input_data)
  const processInputNode = useCallback(async (
    nodeId: string, 
    data: Record<string, unknown>
  ) => {
    setIsTyping(true);
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Suporta diferentes estruturas de configuração do FlowBuilder
    const config = (data.config as Record<string, unknown>) || {};
    const prompt = substituteVariables(
      (config.question as string) ||
      (config.prompt as string) || 
      (data.question as string) ||
      (data.prompt as string) || 
      (data.message as string) || 
      'Digite sua resposta:'
    );

    addMessage({
      id: `msg_${Date.now()}`,
      type: 'bot',
      content: prompt,
      timestamp: new Date(),
      nodeId,
    });

    setIsTyping(false);
    setWaitingForInput(true);
  }, [substituteVariables, addMessage]);

  // Processa nó de delay
  const processDelayNode = useCallback(async (
    nodeId: string,
    data: Record<string, unknown>,
    flowDataRef: PublicFlowData
  ) => {
    const config = (data.config as Record<string, unknown>) || {};
    const seconds = Math.min(
      (config.seconds as number) || (data.seconds as number) || 1, 
      10 // Máximo 10s no público
    );
    setIsTyping(true);
    await new Promise((resolve) => setTimeout(resolve, seconds * 1000));
    setIsTyping(false);
    moveToNextNode(nodeId, flowDataRef);
  }, [moveToNextNode]);

  // Processa nó de condição
  const processConditionNode = useCallback((
    node: FlowNode,
    flowDataRef: PublicFlowData,
    currentVariables: Record<string, string>
  ) => {
    const config = (node.data.config as Record<string, unknown>) || {};
    const conditions = (config.conditions as Array<{ variable: string; operator: string; value: string }>) || 
                       (node.data.conditions as Array<{ variable: string; operator: string; value: string }>) || [];
    
    // Avalia condições
    let matchedHandle = 'default';
    for (let i = 0; i < conditions.length; i++) {
      const cond = conditions[i];
      const varValue = currentVariables[cond.variable] || '';
      
      let matches = false;
      switch (cond.operator) {
        case 'equals':
        case 'equal':
          matches = varValue === cond.value;
          break;
        case 'contains':
          matches = varValue.toLowerCase().includes(cond.value.toLowerCase());
          break;
        case 'not_equals':
        case 'not_equal':
          matches = varValue !== cond.value;
          break;
        case 'greater_than':
          matches = parseFloat(varValue) > parseFloat(cond.value);
          break;
        case 'less_than':
          matches = parseFloat(varValue) < parseFloat(cond.value);
          break;
        case 'is_empty':
          matches = !varValue || varValue.trim() === '';
          break;
        case 'is_not_empty':
          matches = !!varValue && varValue.trim() !== '';
          break;
        default:
          matches = varValue === cond.value;
      }

      if (matches) {
        matchedHandle = `condition_${i}`;
        break;
      }
    }

    // Encontra edge correspondente
    const edge = flowDataRef.flow.edges.find(
      (e) => e.source === node.id && e.sourceHandle === matchedHandle
    ) || flowDataRef.flow.edges.find(
      (e) => e.source === node.id && e.sourceHandle === 'default'
    ) || flowDataRef.flow.edges.find(
      (e) => e.source === node.id
    );

    if (edge) {
      executionQueue.current.push(edge.target);
    }
  }, []);

  // Processa nó de Teste A/B
  const processABTestNode = useCallback((
    node: FlowNode,
    flowDataRef: PublicFlowData
  ) => {
    const config = (node.data.config as Record<string, unknown>) || {};
    const variants = (config.variants as Array<{ id: string; percentage: number }>) || 
                     (node.data.variants as Array<{ id: string; percentage: number }>) || [];
    
    // Seleciona variante baseado nas porcentagens
    const random = Math.random() * 100;
    let accumulated = 0;
    let selectedHandle = 'variant_0';
    
    for (let i = 0; i < variants.length; i++) {
      accumulated += variants[i].percentage;
      if (random <= accumulated) {
        selectedHandle = `variant_${i}`;
        break;
      }
    }
    
    // Encontra edge correspondente
    const edge = flowDataRef.flow.edges.find(
      (e) => e.source === node.id && e.sourceHandle === selectedHandle
    ) || flowDataRef.flow.edges.find(
      (e) => e.source === node.id
    );
    
    if (edge) {
      executionQueue.current.push(edge.target);
    }
  }, []);

  /**
   * Processa nó de definir variável
   * Suporta valores estáticos, referência a outra variável, expressões e datas dinâmicas
   * 
   * RELEASE: 1.1.0 - 2025-12-28 - Adicionado suporte a value_type 'dynamic_date'
   */
  const processSetVariableNode = useCallback(async (
    node: FlowNode,
    flowDataRef: PublicFlowData
  ) => {
    const config = (node.data.config as Record<string, unknown>) || {};
    const varName = (config.variable_name as string) || (node.data.variableName as string);
    const valueType = (config.value_type as string) || 'static';
    let varValue = (config.value as string) || (node.data.value as string) || '';
    
    // Processa valor baseado no tipo
    if (valueType === 'dynamic_date' && config.date_config) {
      // Importação dinâmica para calcular data
      try {
        const { calculateDynamicDate } = await import('@/lib/dynamic-date');
        const dateConfig = config.date_config as {
          base: 'today' | 'start_of_month' | 'end_of_month' | 'start_of_year' | 'end_of_year';
          offset_type: 'none' | 'days' | 'months' | 'years';
          offset_value: number;
          format: string;
        };
        varValue = calculateDynamicDate(dateConfig);
        logger.debug('usePublicFlowChat', `Data dinâmica calculada: ${varValue}`);
      } catch (err) {
        logger.error('usePublicFlowChat', 'Erro ao calcular data dinâmica', err);
      }
    } else if (valueType === 'variable') {
      const sourceVarName = varValue.replace(/\{\{|\}\}/g, '').trim();
      varValue = variables[sourceVarName] !== undefined 
        ? String(variables[sourceVarName]) 
        : varValue;
    } else if (valueType === 'expression') {
      varValue = substituteVariables(varValue);
    } else {
      // static - apenas substitui variáveis se houver
      varValue = substituteVariables(varValue);
    }
    
    if (varName) {
      setVariables((prev) => ({ ...prev, [varName]: varValue }));
    }
    
    moveToNextNode(node.id, flowDataRef);
  }, [variables, substituteVariables, moveToNextNode]);

  // Processa nó de goto (pular)
  const processGotoNode = useCallback((
    node: FlowNode,
    flowDataRef: PublicFlowData
  ) => {
    const config = (node.data.config as Record<string, unknown>) || {};
    const targetNodeId = (config.target_node_id as string) || (node.data.targetNodeId as string);
    
    if (targetNodeId) {
      executionQueue.current.push(targetNodeId);
    } else {
      // Se não tem target, move para próximo normalmente
      moveToNextNode(node.id, flowDataRef);
    }
  }, [moveToNextNode]);

  // Processa nó final
  const processEndNode = useCallback(() => {
    setIsCompleted(true);
    
    addMessage({
      id: `msg_${Date.now()}`,
      type: 'system',
      content: 'Conversa finalizada. Obrigado!',
      timestamp: new Date(),
    });
  }, [addMessage]);

  // Processa um nó específico - mapeia TODOS os tipos do FlowBuilder
  // Processa um nó específico - mapeia TODOS os tipos do FlowBuilder
  const processNode = useCallback(async (node: FlowNode, flowDataRef: PublicFlowData, currentVariables: Record<string, string>) => {
    const nodeType = node.type;

    logger.debug('usePublicFlowChat', 'Processando nó: ' + nodeType + ' (' + node.id + ')');

    switch (nodeType) {
      // Nós de mensagem
      case 'message':
      case 'message_text':
      case 'message_media':
        await processMessageNode(node.id, node.data, flowDataRef);
        break;
      
      // Nós de pergunta com botões/lista
      case 'question':
      case 'question_buttons':
      case 'question_list':
        await processQuestionNode(node.id, node.data, flowDataRef, nodeType);
        break;
      
      // Nó de coleta de dados
      case 'input':
      case 'input_data':
        await processInputNode(node.id, node.data);
        break;
      
      // Delay
      case 'delay':
        await processDelayNode(node.id, node.data, flowDataRef);
        break;
      
      // Condição
      case 'condition':
        processConditionNode(node, flowDataRef, currentVariables);
        break;
      
      // Teste A/B
      case 'ab_test':
        processABTestNode(node, flowDataRef);
        break;
      
      // Definir variável
      case 'set_variable':
        processSetVariableNode(node, flowDataRef);
        break;
      
      // Pular para outro nó
      case 'goto':
        processGotoNode(node, flowDataRef);
        break;
      
      // Nó final
      case 'end':
        processEndNode();
        break;
      
      // ========================================
      // NODES DE RASTREAMENTO (GTM, Meta Pixel, GA4)
      // RELEASE: 1.0.0 - 2026-03-08
      // ========================================
      case 'google_tag_manager':
        (() => {
          const gtmConfig = (node.data.config as Record<string, unknown>) || {};
          const gtmEventName = gtmConfig.event_name as string;
          const gtmVars = (gtmConfig.variables as Record<string, string>) || {};
          if ((window as any).dataLayer && gtmEventName) {
            // Substitui variáveis do fluxo nos valores
            const resolvedVars: Record<string, string> = {};
            Object.entries(gtmVars).forEach(([k, v]) => {
              resolvedVars[k] = substituteVariables(v);
            });
            (window as any).dataLayer.push({ event: gtmEventName, ...resolvedVars });
            logger.debug('usePublicFlowChat', `[GTM] Evento disparado: ${gtmEventName}`);
          }
        })();
        moveToNextNode(node.id, flowDataRef);
        break;

      case 'meta_pixel':
        (() => {
          const pixelConfig = (node.data.config as Record<string, unknown>) || {};
          const pixelEventType = pixelConfig.event_type as string;
          const pixelParams = (pixelConfig.params as Record<string, string>) || {};
          const fbq = (window as any).fbq;
          if (fbq && pixelEventType) {
            // Substitui variáveis do fluxo nos parâmetros
            const resolvedParams: Record<string, string> = {};
            Object.entries(pixelParams).forEach(([k, v]) => {
              resolvedParams[k] = substituteVariables(v);
            });
            if (pixelEventType === 'Custom') {
              const customName = (pixelConfig.custom_event_name as string) || 'CustomEvent';
              fbq('trackCustom', customName, resolvedParams);
              logger.debug('usePublicFlowChat', `[Pixel] Evento custom disparado: ${customName}`);
            } else {
              fbq('track', pixelEventType, resolvedParams);
              logger.debug('usePublicFlowChat', `[Pixel] Evento disparado: ${pixelEventType}`);
            }
          }
        })();
        moveToNextNode(node.id, flowDataRef);
        break;

      case 'google_analytics':
        (() => {
          const ga4Config = (node.data.config as Record<string, unknown>) || {};
          const ga4EventName = ga4Config.event_name as string;
          const ga4Params = (ga4Config.params as Record<string, string>) || {};
          const gtag = (window as any).gtag;
          if (gtag && ga4EventName) {
            // Substitui variáveis do fluxo nos parâmetros
            const resolvedParams: Record<string, string> = {};
            Object.entries(ga4Params).forEach(([k, v]) => {
              resolvedParams[k] = substituteVariables(v);
            });
            gtag('event', ga4EventName, resolvedParams);
            logger.debug('usePublicFlowChat', `[GA4] Evento disparado: ${ga4EventName}`);
          }
        })();
        moveToNextNode(node.id, flowDataRef);
        break;

      // ========================================
      // NODE DE INCORPORAÇÃO (EMBED/IFRAME)
      // RELEASE: 1.0.0 - 2026-03-08
      // ========================================
      case 'embed':
        (() => {
          const embedConfig = (node.data.config as Record<string, unknown>) || {};
          // Normaliza URL para formato embed (YouTube/Vimeo) evitando bloqueio X-Frame-Options
          const embedUrl = normalizeEmbedUrl(substituteVariables((embedConfig.url as string) || ''));
          const embedHeight = (embedConfig.height as number) || 400;
          
          if (embedUrl) {
            // Adiciona mensagem com tipo embed para renderizar iframe
            const embedMsg: ChatMessage = {
              id: `msg-${Date.now()}-embed`,
              type: 'bot',
              content: '',
              timestamp: new Date(),
              mediaType: 'embed',
              mediaUrl: embedUrl,
              embedHeight,
              nodeId: node.id,
            };
            setMessages(prev => [...prev, embedMsg]);
            logger.debug('usePublicFlowChat', `[Embed] iframe renderizado: ${embedUrl}`);
          }
        })();
        moveToNextNode(node.id, flowDataRef);
        break;

      // Ações não executadas no chat público (sem contexto de atendimento)
      case 'add_tag':
      case 'remove_tag':
      case 'update_contact':
      case 'transfer_human':
      case 'transfer_department':
      case 'webhook':
        // Apenas move para o próximo nó
        logger.debug('usePublicFlowChat', 'Nó de ação ignorado no chat público: ' + nodeType);
        moveToNextNode(node.id, flowDataRef);
        break;
      
      // Nó start - não processa, apenas pula
      case 'start':
        moveToNextNode(node.id, flowDataRef);
        break;
      
      // Node de horário comercial - verifica se está dentro do horário configurado
      case 'business_hours':
        (() => {
          const bhConfig = (node.data.config as Record<string, unknown>) || {};
          const bhTimezone = (bhConfig?.timezone as string) || 'America/Sao_Paulo';
          const bhSchedule = bhConfig?.schedule as Record<string, { enabled: boolean; start_time: string; end_time: string }> | undefined;
          
          // Se não configurado, segue para 'closed'
          if (!bhSchedule) {
            logger.warn('usePublicFlowChat', '[business_hours] Schedule não configurado');
            const closedEdge = flowDataRef.flow?.edges?.find(
              e => e.source === node.id && e.sourceHandle === 'closed'
            );
            if (closedEdge) {
              executionQueue.current.push(closedEdge.target);
            }
            return;
          }
          
          // Obtém data/hora atual no fuso configurado
          const now = new Date();
          const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: bhTimezone,
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            weekday: 'long',
          });
          
          const parts = formatter.formatToParts(now);
          const weekday = parts.find(p => p.type === 'weekday')?.value?.toLowerCase();
          const hour = parts.find(p => p.type === 'hour')?.value || '00';
          const minute = parts.find(p => p.type === 'minute')?.value || '00';
          const currentTime = `${hour}:${minute}`;
          
          // Mapeamento de weekday para chave do schedule
          const dayKeyMap: Record<string, string> = {
            'sunday': 'sunday',
            'monday': 'monday',
            'tuesday': 'tuesday',
            'wednesday': 'wednesday',
            'thursday': 'thursday',
            'friday': 'friday',
            'saturday': 'saturday',
          };
          
          const dayKey = dayKeyMap[weekday || 'monday'];
          const dayConfig = bhSchedule[dayKey as keyof typeof bhSchedule];
          
          // Verifica se está dentro do horário
          let isOpen = false;
          
          if (dayConfig?.enabled) {
            const startTime = dayConfig.start_time || '00:00';
            const endTime = dayConfig.end_time || '23:59';
            
            // Compara strings HH:mm (funciona para ordenação lexicográfica)
            isOpen = currentTime >= startTime && currentTime < endTime;
          }
          
          logger.info('usePublicFlowChat', `[business_hours] timezone=${bhTimezone}, day=${weekday}, time=${currentTime}, isOpen=${isOpen}`);
          
          // Encontra próximo node baseado no resultado
          const sourceHandle = isOpen ? 'open' : 'closed';
          const nextEdge = flowDataRef.flow?.edges?.find(
            e => e.source === node.id && e.sourceHandle === sourceHandle
          );
          
          if (nextEdge) {
            executionQueue.current.push(nextEdge.target);
          }
        })();
        break;
      
      default:
        // Nó desconhecido - loga e pula
        logger.warn('usePublicFlowChat', 'Tipo de nó não suportado: ' + nodeType);
        moveToNextNode(node.id, flowDataRef);
    }
  }, [
    processMessageNode, 
    processQuestionNode, 
    processInputNode, 
    processDelayNode,
    processConditionNode,
    processABTestNode,
    processSetVariableNode,
    processGotoNode,
    processEndNode,
    moveToNextNode,
    substituteVariables
  ]);

  // Atualiza ref do processNode para uso no loadFlow (evita dependência circular)
  useEffect(() => {
    processNodeRef.current = processNode;
  }, [processNode]);

  // Processa fila de execução
  const processQueue = useCallback(async () => {
    if (isProcessing.current || executionQueue.current.length === 0 || !flowData) {
      return;
    }

    isProcessing.current = true;
    const nodeId = executionQueue.current.shift()!;
    const node = flowData.flow.nodes.find((n) => n.id === nodeId);

    if (!node) {
      logger.warn('usePublicFlowChat', 'Nó não encontrado: ' + nodeId);
      isProcessing.current = false;
      return;
    }

    setCurrentNodeId(nodeId);

    try {
      await processNode(node, flowData, variables);
    } catch (err) {
      logger.error('usePublicFlowChat', 'Erro ao processar nó: ' + String(err));
    }

    isProcessing.current = false;

    // Continua processando se não estiver aguardando input
    // IMPORTANTE: usar o ref para não cair em stale state (closure com valor antigo)
    if (!waitingForInputRef.current && executionQueue.current.length > 0) {
      setTimeout(() => processQueue(), 300);
    }
  }, [flowData, processNode, variables]);

  // Salva sessão no banco
  const saveSession = useCallback(async (completed: boolean) => {
    if (!flowData) return;

    try {
      await supabase.rpc('save_flow_chat_session', {
        p_flow_id: flowData.flow.id,
        p_visitor_id: visitorId.current,
        p_variables: variables,
        p_current_node_id: currentNodeId,
        p_messages: messages.map((m) => ({
          id: m.id,
          type: m.type,
          content: m.content,
          timestamp: m.timestamp.toISOString(),
        })),
        p_is_completed: completed,
      });
    } catch (err) {
      logger.error('usePublicFlowChat', 'Erro ao salvar sessão: ' + String(err));
    }
  }, [flowData, variables, currentNodeId, messages]);

  // Carrega fluxo público pelo tenant_slug + flow_slug
  const loadFlow = useCallback(async () => {
    // Evita carregamento duplicado (corrige loop infinito)
    if (!tenantSlug || !flowSlug || isLoadedRef.current) return;
    isLoadedRef.current = true;

    try {
      setIsLoading(true);
      setError(null);

      logger.debug('usePublicFlowChat', `Carregando fluxo: tenant=${tenantSlug}, flow=${flowSlug}`);

      // Busca fluxo via RPC usando tenant_slug + flow_slug
      const { data, error: rpcError } = await supabase.rpc('get_public_flow_by_slug', {
        p_tenant_slug: tenantSlug,
        p_flow_slug: flowSlug,
      });

      if (rpcError) {
        logger.error('usePublicFlowChat', 'Erro ao carregar fluxo: ' + rpcError.message);
        setError('Fluxo não encontrado ou não está disponível');
        isLoadedRef.current = false; // Permite retry em caso de erro
        return;
      }

      if (!data) {
        setError('Fluxo não encontrado');
        isLoadedRef.current = false;
        return;
      }

      const parsedData = data as unknown as PublicFlowData;
      logger.debug('usePublicFlowChat', 'Fluxo carregado: ' + parsedData.flow.name + ' (nós: ' + parsedData.flow.nodes.length + ')');

      setFlowData(parsedData);

      // Incrementa contador de visualizações (apenas 1 vez)
      await supabase.rpc('increment_flow_views', { p_flow_id: parsedData.flow.id });

      // Inicializa variáveis do fluxo
      const initialVars: Record<string, string> = {};
      parsedData.flow.variables?.forEach((v) => {
        initialVars[v.name] = v.defaultValue || '';
      });

      // Popula variáveis UTM automaticamente para uso em nodes via {{utm_source}} etc.
      const utmParams = getUTMParams();
      const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid', 'ref'] as const;
      utmKeys.forEach((key) => {
        if (utmParams[key]) {
          initialVars[key] = utmParams[key]!;
        }
      });

      setVariables(initialVars);

      // Busca nó inicial (start)
      const startNode = parsedData.flow.nodes.find((n) => n.type === 'start');
      logger.debug('usePublicFlowChat', 'Nó start encontrado: ' + (startNode?.id || 'nenhum'));

      if (startNode) {
        // Encontra próximo nó após start
        const nextEdge = parsedData.flow.edges.find((e) => e.source === startNode.id);
        if (nextEdge) {
          logger.debug('usePublicFlowChat', 'Iniciando execução a partir do nó: ' + nextEdge.target);
          setCurrentNodeId(nextEdge.target);
          executionQueue.current.push(nextEdge.target);
          
          // Aguarda um ciclo para garantir que o estado foi atualizado
          setTimeout(() => {
            isProcessing.current = false;
            // Chama processQueue diretamente com dados atualizados usando ref
            const processInitialQueue = async () => {
              if (executionQueue.current.length === 0) return;
              
              isProcessing.current = true;
              const nodeId = executionQueue.current.shift()!;
              const node = parsedData.flow.nodes.find((n) => n.id === nodeId);
              
              if (node && processNodeRef.current) {
                setCurrentNodeId(nodeId);
                try {
                  await processNodeRef.current(node, parsedData, initialVars);
                } catch (err) {
                  logger.error('usePublicFlowChat', 'Erro ao processar nó inicial: ' + String(err));
                }
              }
              
              isProcessing.current = false;
              
              // Continua processando fila
              if (executionQueue.current.length > 0) {
                setTimeout(processInitialQueue, 300);
              }
            };
            
            processInitialQueue();
          }, 100);
        }
      }
    } catch (err) {
      logger.error('usePublicFlowChat', 'Erro: ' + String(err));
      setError('Erro ao carregar fluxo');
      isLoadedRef.current = false; // Permite retry em caso de erro
    } finally {
      setIsLoading(false);
    }
  }, [tenantSlug, flowSlug]); // Dependências: tenant_slug e flow_slug

  // Envia resposta do usuário
  const sendUserMessage = useCallback((content: string, buttonId?: string) => {
    if (!waitingForInput || isCompleted || !flowData) return;

    // Adiciona mensagem do usuário
    addMessage({
      id: `user_${Date.now()}`,
      type: 'user',
      content,
      timestamp: new Date(),
    });

    // Encontra nó atual
    const currentNode = flowData.flow.nodes.find((n) => n.id === currentNodeId);
    if (!currentNode) return;

    const nodeType = currentNode.type;
    const config = (currentNode.data.config as Record<string, unknown>) || {};

    // Salva variável se for input node
    if (nodeType === 'input' || nodeType === 'input_data') {
      const varName = (config.variable_name as string) || (currentNode.data.variableName as string);
      if (varName) {
        setVariables((prev) => ({ ...prev, [varName]: content }));
      }
    }

    // Se foi clique em botão, encontra edge correspondente
    if (buttonId && (nodeType === 'question' || nodeType === 'question_buttons' || nodeType === 'question_list')) {
      const varName = (config.variable_name as string) || (currentNode.data.variableName as string);
      if (varName) {
        setVariables((prev) => ({ ...prev, [varName]: content }));
      }

      const edge = flowData.flow.edges.find(
        (e) => e.source === currentNodeId && e.sourceHandle === buttonId
      ) || flowData.flow.edges.find(
        (e) => e.source === currentNodeId
      );

      if (edge) {
        // Atualiza o ref imediatamente para garantir continuação do fluxo
        waitingForInputRef.current = false;
        setWaitingForInput(false);

        executionQueue.current.push(edge.target);
        setTimeout(() => processQueue(), 300);
      }
    } else {
      // Input de texto livre
      // Atualiza o ref imediatamente para garantir continuação do fluxo
      waitingForInputRef.current = false;
      setWaitingForInput(false);

      moveToNextNode(currentNodeId!, flowData);
      setTimeout(() => processQueue(), 300);
    }

    // Salva sessão
    saveSession(false);
  }, [waitingForInput, isCompleted, flowData, currentNodeId, processQueue, addMessage, moveToNextNode, saveSession]);

  // Carrega fluxo ao montar
  useEffect(() => {
    loadFlow();
  }, [loadFlow]);

  return {
    flowData,
    messages,
    variables,
    isLoading,
    isTyping,
    error,
    isCompleted,
    waitingForInput,
    sendUserMessage,
    config: flowData?.config,
    /** Configurações de rastreamento (GTM, Pixel, GA4, custom head) */
    tracking: flowData?.tracking || null,
  };
}
